/**
 * Support tickets and disputes — `/v1/admin/support*`, `/v1/admin/disputes*`,
 * `/v1/admin/sla-policies`.
 *
 * **There is no SLA update hook, deliberately.** An SLA edited in place would silently move the
 * clocks of every ticket that pinned it, which is what versioning exists to prevent — and the
 * failure would surface weeks later as a breach report that looked wrong. Publishing a new
 * version is the only write this module offers.
 *
 * **Opening or resolving a dispute moves a pandit's money.** The hooks are named for what they
 * do rather than for the HTTP verb, so a screen cannot call one thinking it is a status change.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type AdminTicketRow = components['schemas']['AdminTicketRow'];
export type AdminTicketDetail = components['schemas']['AdminTicketDetail'];
export type AdminTicketMessageRow = components['schemas']['AdminTicketMessageRow'];
export type DisputeRow = components['schemas']['DisputeRow'];
export type DisputeDetail = components['schemas']['DisputeDetail'];
export type DisputeBundle = components['schemas']['DisputeBundle'];
export type SlaPolicyRow = components['schemas']['SlaPolicyRow'];
type TicketList = components['schemas']['AdminTicketListResponse'];
export type AdminTicketOpenIn = components['schemas']['AdminTicketOpenIn'];
export type AdminEvidenceIn = components['schemas']['AdminEvidenceIn'];
export type EvidenceRow = components['schemas']['EvidenceRow'];
type DisputeList = components['schemas']['DisputeListResponse'];
type SlaList = components['schemas']['SlaPolicyListResponse'];
export type SupportMacroRow = components['schemas']['SupportMacroRow'];
type MacroList = components['schemas']['SupportMacroListResponse'];
export type OutcomeMatrix = components['schemas']['OutcomeMatrix'];
type AgentSummary = components['schemas']['AgentSummaryResponse'];
type SlaReport = components['schemas']['SlaReportResponse'];

export interface TicketQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  ticket_status?: string;
  priority?: string;
}

/** Exported for its own test: an absent filter and an explicit one are different requests. */
export function ticketSearch(params: TicketQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.ticket_status !== undefined) search.set('ticket_status', params.ticket_status);
  if (params.priority !== undefined) search.set('priority', params.priority);
  return search.toString();
}

export function useTickets(params: TicketQuery) {
  return useQuery({
    queryKey: keys.support.tickets(params),
    queryFn: () =>
      authedClient.request<TicketList>(`/v1/admin/support/tickets?${ticketSearch(params)}`),
  });
}

export function useTicket(code: string | null) {
  return useQuery({
    queryKey: keys.support.ticket(code ?? ''),
    queryFn: () =>
      authedClient.request<AdminTicketDetail>(`/v1/admin/support/tickets/${code}`),
    enabled: code !== null,
  });
}

function useSupportMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      client.invalidateQueries({ queryKey: keys.support.all() });
      client.invalidateQueries({ queryKey: keys.disputes.all() });
    },
  });
}

/** `internal: true` writes a note the user never sees — and it never counts as a first reply. */
export function useReplyToTicket() {
  return useSupportMutation(
    ({ code, body, internal }: { code: string; body: string; internal: boolean }) =>
      authedClient.request<AdminTicketMessageRow>(
        `/v1/admin/support/tickets/${code}/messages`,
        { method: 'POST', body: { body, internal } },
      ),
  );
}

export function useSetTicketStatus() {
  return useSupportMutation(({ code, status }: { code: string; status: string }) =>
    authedClient.request<AdminTicketRow>(`/v1/admin/support/tickets/${code}/status`, {
      method: 'POST',
      body: { status },
    }),
  );
}

export function useAssignTicket() {
  return useSupportMutation(({ code, adminId }: { code: string; adminId: number }) =>
    authedClient.request<AdminTicketRow>(`/v1/admin/support/tickets/${code}/assign`, {
      method: 'POST',
      body: { admin_id: adminId },
    }),
  );
}

/** Creates a platform-raised dispute, which holds the pandit's payout. Named for that. */
export function useEscalateToDispute() {
  return useSupportMutation(({ code }: { code: string }) =>
    authedClient.request<DisputeRow>(
      `/v1/admin/support/tickets/${code}/escalate-to-dispute`,
      { method: 'POST' },
    ),
  );
}

// ── Disputes ────────────────────────────────────────────────────────────────

export interface DisputeQuery extends Record<string, unknown> {
  page: number;
  page_size: number;
  dispute_status?: string;
}

export function disputeSearch(params: DisputeQuery): string {
  const search = new URLSearchParams({
    page: String(params.page),
    page_size: String(params.page_size),
  });
  if (params.dispute_status !== undefined) {
    search.set('dispute_status', params.dispute_status);
  }
  return search.toString();
}

export function useDisputes(params: DisputeQuery) {
  return useQuery({
    queryKey: keys.disputes.list(params),
    queryFn: () =>
      authedClient.request<DisputeList>(`/v1/admin/disputes?${disputeSearch(params)}`),
  });
}

/** §5.5's matrix as the server enforces it, and support's money ceilings (admin doc 18 §2). */
export function useOutcomeMatrix() {
  return useQuery({
    queryKey: keys.disputes.matrix(),
    queryFn: () => authedClient.request<OutcomeMatrix>('/v1/admin/disputes/outcome-matrix'),
  });
}

export function useDispute(code: string | null) {
  return useQuery({
    queryKey: keys.disputes.one(code ?? ''),
    queryFn: () => authedClient.request<DisputeDetail>(`/v1/admin/disputes/${code}`),
    enabled: code !== null,
  });
}

export function useAssignDispute() {
  return useSupportMutation(({ code, adminId }: { code: string; adminId: number }) =>
    authedClient.request<DisputeRow>(`/v1/admin/disputes/${code}/assign`, {
      method: 'POST',
      body: { admin_id: adminId },
    }),
  );
}

/**
 * The one-screen bundle (doc 34 §5.4).
 *
 * **Reading it mints nothing.** The evidence section says what exists and whether this admin
 * already holds a live grant; opening the photographs stays the deliberate, logged act it was
 * built as. A hook that fetched links because a tab rendered would fill a devotee's access
 * trail with looks nobody took.
 */
export function useDisputeBundle(code: string | null, enabled: boolean) {
  return useQuery({
    queryKey: keys.disputes.bundle(code ?? ''),
    queryFn: () => authedClient.request<DisputeBundle>(`/v1/admin/disputes/${code}/bundle`),
    enabled: code !== null && enabled,
  });
}

export function useSetDisputeStatus() {
  return useSupportMutation(({ code, status }: { code: string; status: string }) =>
    authedClient.request<DisputeRow>(`/v1/admin/disputes/${code}/status`, {
      method: 'POST',
      body: { status },
    }),
  );
}

/**
 * The decision. This is what ends the payout hold, and above the four-eyes threshold the
 * server refuses without `secondApproverAdminId` — and refuses again if that is the same
 * person. The screen offers the field; the server is what enforces it.
 */
export function useResolveDispute() {
  return useSupportMutation(
    ({
      code,
      outcome,
      rationale,
      refundAmountPaise,
      payoutForfeitPaise,
      secondApproverAdminId,
    }: {
      code: string;
      outcome: string;
      rationale: string;
      refundAmountPaise?: number;
      payoutForfeitPaise?: number;
      secondApproverAdminId?: number;
    }) => {
      const query =
        secondApproverAdminId === undefined
          ? ''
          : `?second_approver_admin_id=${secondApproverAdminId}`;
      return authedClient.request(`/v1/admin/disputes/${code}/resolve${query}`, {
        method: 'POST',
        body: {
          outcome,
          rationale,
          refund_amount_paise: refundAmountPaise ?? null,
          payout_forfeit_paise: payoutForfeitPaise ?? null,
        },
      });
    },
  );
}

// ── SLA ─────────────────────────────────────────────────────────────────────

export function useSlaPolicies() {
  return useQuery({
    queryKey: keys.support.sla(),
    queryFn: () => authedClient.request<SlaList>('/v1/admin/sla-policies'),
  });
}

/** Publishing a NEW version. There is no in-place edit — see the module docstring. */
export function usePublishSlaVersion() {
  return useSupportMutation(
    (body: {
      category: string;
      priority: string;
      first_response_minutes: number;
      resolution_minutes: number;
      escalation_admin_role: string;
      pauses_on_pending_customer: boolean;
    }) =>
      authedClient.request<SlaPolicyRow>('/v1/admin/sla-policies', {
        method: 'POST',
        body,
      }),
  );
}

// ── Reply macros (product doc 13 §5) ──────────────────────────────────────────

export function useSupportMacros() {
  return useQuery({
    queryKey: keys.support.macros(),
    queryFn: () => authedClient.request<MacroList>('/v1/admin/support/macros'),
  });
}

/** Edits the row in place, or writes its Hindi. The server checks placeholders and the lint. */
export function useSaveSupportMacro() {
  return useSupportMutation(
    ({ code, locale, title, body }: { code: string; locale: string; title: string; body: string }) =>
      authedClient.request<SupportMacroRow>(`/v1/admin/support/macros/${code}/${locale}`, {
        method: 'PUT',
        body: { title, body },
      }),
  );
}

/** Phone intake (doc 21 §3.3): the agent opens it, the ticket belongs to the caller. */
export function usePhoneIntake() {
  return useSupportMutation((body: AdminTicketOpenIn) =>
    authedClient.request<AdminTicketRow>('/v1/admin/support/tickets', { method: 'POST', body }),
  );
}

/** Link a completion photo, a ticket attachment or a note to a live dispute (doc 18 §3.2). */
export function useLinkDisputeEvidence() {
  return useSupportMutation(({ code, ...body }: AdminEvidenceIn & { code: string }) =>
    authedClient.request<EvidenceRow>(`/v1/admin/disputes/${code}/evidence`, { method: 'POST', body }),
  );
}

// ── The weekly review (admin doc 21 §4) ──────────────────────────────────────

export function useAgentSummary() {
  return useQuery({
    queryKey: keys.support.agents(),
    queryFn: () => authedClient.request<AgentSummary>('/v1/admin/support/agents/summary'),
  });
}

export function useSlaReport() {
  return useQuery({
    queryKey: keys.support.slaReport(),
    queryFn: () => authedClient.request<SlaReport>('/v1/admin/support/sla-report'),
  });
}
